/**
 * Corporate Dashboard · authenticated member's home.
 *
 * Layout mirrors the MC hero-then-Mira pattern per user's screenshots:
 *   1. WhatsApp Concierge HERO card       (primary CTA · brass)
 *   2. Mira chat suggestion + Chatbase iframe
 *   3. Pillar cards grid — links deep into /table, /stay, /fit, /care,
 *      /airport, /escape, /enjoy, /exclusive (Maisons).
 *      Clubs card = paid-upgrade upsell that opens
 *      https://mindescapes.club/mindescapes ↗
 *
 * Every pillar card links to the existing LC page for that pillar so
 * the corporate member enjoys the full LC feature set from day one.
 * When they submit a brief from any pillar while signed into the
 * corporate session (cookie), the backend will tag it with sponsor.
 */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { corporateApi } from "@/lib/corporate-api";
import CorporateShell from "./CorporateShell";
import MiraEmbed, { CHATBASE_ORIGIN } from "@/components/MiraEmbed";

const T = {
  bg:   "#F4EFE6", card: "#FCFAF4", ink:  "#211C15",
  ink2: "#5C5447", ink3: "#8A8071",
  line: "#E5DCCB", line2:"#D8CDB7",
  brass:"#8B6914", brassSoft: "#B69152",
  wa:   "#25D366",
};

// Pillar definitions — mirrors LC home tiles, minus /walks (nested in fit).
const PILLAR_CARDS = [
  { key: "table",   href: "/table",     title: "Name the place.",
    accent: "We hold the table.",
    body:  "Search any restaurant on earth. Verified · booking-first.",
    cta:   "Find the table" },
  { key: "escape",  href: "/escape",    title: "Where would you",
    accent: "escape?",
    body:  "Tours, activities and attractions — the Concierge® finds your right door.",
    cta:   "Find the escape" },
  { key: "enjoy",   href: "/enjoy",     title: "Name the night.",
    accent: "We open the door.",
    body:  "Live events, concerts, an unforgettable evening — wherever you are.",
    cta:   "Find the night" },
  { key: "stay",    href: "/stay",      title: "Anywhere on earth.",
    accent: "We find the stay.",
    body:  "Universal hotel search across 200+ countries — real photos, ratings and the right room.",
    cta:   "Find the stay" },
  { key: "fit",     href: "/fit",       title: "Move, restore,",
    accent: "feel your best.",
    body:  "Spas, yoga, gyms, recovery and pools — the Concierge® finds your right space.",
    cta:   "Find your fit" },
  { key: "airport", href: "/airport",   title: "Land, lounge,",
    accent: "travel with ease.",
    body:  "Airport lounges, fast-track and transfers — the Concierge® smooths every journey.",
    cta:   "Find the lounge" },
  { key: "maison",  href: "/exclusive", title: "Maisons across",
    accent: "the globe.",
    body:  "Florists, jewellers, galleries, tailors, boutiques and wine merchants — the Concierge® sources what's worth sourcing.",
    cta:   "Find Maisons" },
  { key: "care",    href: "/care",      title: "Looked after,",
    accent: "wherever you are.",
    body:  "Health, wellbeing and personal care — the Concierge® arranges trusted support, quietly.",
    cta:   "Arrange care" },
  { key: "errands", href: "/errands",   title: "Life admin,",
    accent: "quietly handled.",
    body:  "Passport · RTO · bank runs · home coordination. Our team handles it. You get updates on WhatsApp.",
    cta:   "Send an errand" },
  { key: "dreamweaver", href: "/dreamweaver", title: "What have you",
    accent: "always wanted?",
    body:  "Whisper a dream — a private journal, or one you'd like the Concierge® to hold with you.",
    cta:   "Whisper a dream" },
];

const MIRA_SUGGESTIONS = [
  "Plan a holiday to Paris for two",
  "I've lost my passport in Tokyo",
  "Find a last-minute anniversary gift in London",
  "Book a table in Mumbai tonight",
];

export default function CorporateDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [me,  setMe]  = useState(null);
  const [err, setErr] = useState(null);
  const [chipToast, setChipToast] = useState(null); // { text, fallback } | null
  const [drawerPillar, setDrawerPillar] = useState(null); // { key, title, href } | null
  const [chipFlashing, setChipFlashing] = useState(false);
  const miraIframeRef = useRef(null);

  // Lock body scroll while the drawer is open (matches MC pattern).
  useEffect(() => {
    if (!drawerPillar) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setDrawerPillar(null); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [drawerPillar]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let res;
        try {
          res = await corporateApi.me();
        } catch (e1) {
          // v3.47 · Founder ruling 04 Jul 2026 — tenants flagged
          // `guest_mode: true` (currently /demo) skip the OTP flow. On
          // 401, try minting a guest session and re-fetch /me. If the
          // tenant doesn't have guest_mode enabled the guest endpoint
          // returns 403 and we fall back to the sign-in redirect.
          if (e1.status === 401) {
            try {
              await corporateApi.guestSession(slug);
              res = await corporateApi.me();
            } catch (_) {
              throw e1;
            }
          } else {
            throw e1;
          }
        }
        if (!alive) return;
        if (res.needs_terms_accept) {
          navigate(`/${slug}/terms`, { replace: true });
          return;
        }
        setMe(res);
      } catch (e) {
        if (e.status === 401) {
          navigate(`/${slug}/sign-in`, { replace: true });
        } else {
          setErr(e.message || "Something went wrong.");
        }
      }
    })();
    return () => { alive = false; };
  }, [slug, navigate]);

  // v3.47 · MC-agent attribution — drop a `chatbase_identities` breadcrumb
  // the moment the Mira widget mounts so the Chatbase poller can match
  // this visitor's convo → Zoho ticket with real name + sponsor tag.
  useEffect(() => {
    if (!me?.member) return;
    const m = me.member;
    const t = me.tenant || {};
    const cardish = m.id || m.email || `guest-${slug}`;
    corporateApi.miraChatbaseIdentity({
      tenant:          t.slug || slug,
      card_no:         cardish,
      name:            [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email,
      email:           m.email,
      phone:           m.phone || m.whatsapp || null,
      programme:       t.display_name,
      sponsor_company: t.display_name || t.legal_name,
    }).catch(() => { /* best-effort */ });
  }, [me, slug]);

  if (err) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh",
                    display: "flex", alignItems: "center",
                    justifyContent: "center", color: T.ink3,
                    padding: 24 }}>
        {err}
      </div>
    );
  }
  if (!me) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh",
                    display: "flex", alignItems: "center",
                    justifyContent: "center", color: T.ink3,
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontStyle: "italic" }}>
        Loading your Concierge®…
      </div>
    );
  }

  const { tenant, member } = me;
  const wa = tenant.concierge_whatsapp || "";
  const waHref = wa
    ? `https://wa.me/${wa.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
        `Hello Concierge, this is ${member.first_name || member.email} from ${tenant.display_name}.`
      )}`
    : null;
  const emailHref = tenant.concierge_email
    ? `mailto:${tenant.concierge_email}?subject=Concierge request · ${tenant.display_name}`
    : null;
  const enabled = new Set(tenant.benefits_enabled || []);
  const visible = PILLAR_CARDS.filter((p) => enabled.has(p.key));

  return (
    <CorporateShell tenant={tenant} member={member}>
      {/* ── HERO · WhatsApp Concierge ── */}
      <section data-testid="corp-dash-hero"
               style={{ marginTop: 8, marginBottom: 40 }}>
        <div style={{
          background: T.ink, color: T.bg,
          borderRadius: 20, padding: "clamp(24px, 5vw, 40px) clamp(20px, 5vw, 44px)",
          boxShadow: "0 20px 40px rgba(33,28,21,0.14)",
        }}>
          <p style={{ letterSpacing: "0.16em", fontSize: 11, color: T.brassSoft,
                      textTransform: "uppercase", margin: "0 0 14px",
                      fontWeight: 700 }}>
            ✧ Your Concierge® · on duty
          </p>
          <h1 style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: "clamp(26px, 6vw, 50px)", fontWeight: 500,
            lineHeight: 1.08, margin: "0 0 18px",
            wordBreak: "break-word",
          }}>
            {greetingFor(member)}, {member.first_name || "there"}.
            <br />
            <em style={{ color: T.brassSoft }}>
              Ask your Concierge® anything, anywhere.
            </em>
          </h1>
          <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.5,
                      margin: "0 0 28px", maxWidth: 640 }}>
            From Mumbai to Tokyo &mdash; across 180 countries, your Concierge&reg;
            will guide you through anything you need, as long as it&apos;s
            legal and possible.
          </p>
        </div>

        {/* WhatsApp / Call / Email card */}
        <div data-testid="corp-dash-wa-card"
             className="corp-dash-wa-card"
             style={{
          background: T.card, border: `1px solid ${T.line}`,
          borderRadius: 20, padding: "24px 20px",
          marginTop: -8, position: "relative", zIndex: 1,
          display: "grid", gridTemplateColumns: "1fr", gap: 20,
          alignItems: "center",
        }}>
          <style>{`
            @media (min-width: 720px) {
              .corp-dash-wa-card { grid-template-columns: 1fr auto !important;
                                   padding: 28px 32px !important; gap: 24px !important; }
            }
          `}</style>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8,
                          fontSize: 12, color: T.ink3, marginBottom: 8 }}>
              <span style={{ display: "inline-block", width: 8, height: 8,
                             background: T.wa, borderRadius: "50%" }} />
              <span>Online now · Mon–Sun, 9 AM–9 PM IST</span>
            </div>
            <h2 style={{ fontFamily: "'Fraunces', Georgia, serif",
                         fontSize: 24, fontWeight: 500, color: T.ink,
                         margin: "0 0 8px" }}>
              A real Concierge®, always at hand.
            </h2>
            <p style={{ color: T.ink2, fontSize: 14, lineHeight: 1.55,
                        margin: "0 0 6px", maxWidth: 480 }}>
              Anything beyond Mira &mdash; a visa in a hurry, a table that&apos;s
              fully booked, a gift that must arrive tonight &mdash; your live
              Concierge&reg; quietly takes over and sees it through.
            </p>
            {(wa || tenant.concierge_email) && (
              <p style={{ fontSize: 12, color: T.ink3, marginTop: 8,
                          fontFamily: "ui-monospace, monospace" }}>
                {wa}
                {wa && tenant.concierge_email ? " · " : ""}
                {tenant.concierge_email}
              </p>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {waHref && (
              <a
                data-testid="corp-dash-wa-btn"
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: T.wa, color: "#fff", border: 0,
                  borderRadius: 999, padding: "14px 22px", fontSize: 13.5,
                  fontWeight: 700, letterSpacing: "0.06em",
                  textDecoration: "none", textAlign: "center",
                  boxShadow: "0 8px 20px rgba(37,211,102,0.24)",
                  display: "block",
                }}
              >
                💬 WhatsApp your Concierge®
              </a>
            )}
            {emailHref && (
              <a
                data-testid="corp-dash-email-btn"
                href={emailHref}
                style={{
                  background: "transparent", color: T.ink2,
                  border: `1px solid ${T.line2}`, borderRadius: 999,
                  padding: "10px 20px", fontSize: 12, fontWeight: 600,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  textDecoration: "none", textAlign: "center",
                }}
              >
                ✉ Email the Concierge®
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── MIRA suggestion pills + live chatbase embed ── */}
      <section data-testid="corp-dash-mira"
               style={{ marginBottom: 40, paddingTop: 24,
                        borderTop: `1px solid ${T.line}` }}>
        <p style={{ letterSpacing: "0.16em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 16px",
                    fontWeight: 700 }}>
          Try asking Mira
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10,
                      marginBottom: 24 }}>
          {MIRA_SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              data-testid={`corp-dash-mira-suggestion-${i}`}
              type="button"
              onClick={async () => {
                // MC-agent contract: Chatbase's iframe supports a public
                // postMessage API. Post `{type:"sendMessage", message}` to
                // the iframe's contentWindow with the exact Chatbase origin.
                // If postMessage fails (blocked, iframe not ready), fall back
                // to clipboard-copy so the tap never feels broken.
                let seeded = false;
                try {
                  const w = miraIframeRef.current?.contentWindow;
                  if (w) {
                    w.postMessage(
                      { type: "sendMessage", message: s },
                      CHATBASE_ORIGIN
                    );
                    seeded = true;
                  }
                } catch (_) { /* cross-origin blocked · fall through */ }
                if (!seeded) {
                  try {
                    if (navigator.clipboard && window.isSecureContext) {
                      await navigator.clipboard.writeText(s);
                    } else {
                      const ta = document.createElement("textarea");
                      ta.value = s;
                      ta.setAttribute("readonly", "");
                      ta.style.position = "absolute"; ta.style.left = "-9999px";
                      document.body.appendChild(ta);
                      ta.select();
                      try { document.execCommand("copy"); } catch (_) { /* ignore */ }
                      document.body.removeChild(ta);
                    }
                  } catch (_) { /* ignore · toast still fires */ }
                }
                // UX polish: scroll widget into view + flash a highlight ring
                const el = document.querySelector("[data-testid='corp-dash-mira-embed']");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                setChipFlashing(true);
                setTimeout(() => setChipFlashing(false), 900);
                setChipToast({ text: s, fallback: !seeded });
                setTimeout(() => setChipToast(null), seeded ? 2600 : 4200);
              }}
              style={{
                background: T.card, color: T.ink,
                border: `1px solid ${T.line}`, borderRadius: 999,
                padding: "10px 18px", fontSize: 13, fontWeight: 500,
                cursor: "pointer", fontFamily: "inherit",
                transition: "background 160ms ease, transform 160ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.card; }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Live Chatbase embed · same chatbot as lesconcierges.com.
            Visitor identity passed so the concierge team sees the
            employee's name + email + sponsor company on every Zoho
            ticket that comes out of a Mira chat. */}
        <div
          data-testid="corp-dash-mira-embed"
          style={{
            background: T.card,
            border: `1px solid ${chipFlashing ? T.brass : T.line}`,
            borderRadius: 16, padding: "12px 14px 6px",
            minHeight: 700,
            boxShadow: chipFlashing
              ? "0 0 0 4px rgba(139,105,20,0.24), 0 12px 32px rgba(139,105,20,0.16)"
              : "none",
            transition: "border-color 260ms ease, box-shadow 260ms ease",
          }}
        >
          <MiraEmbed
            ref={miraIframeRef}
            visitor={{
              id:       member.id,
              name:     [member.first_name, member.last_name].filter(Boolean).join(" ") || member.email,
              email:    member.email,
              whatsapp: null,
            }}
          />
        </div>
      </section>

      {/* ── Chip-sent toast · warm, LUX. Two modes:
          · sent → "Mira is curating your ask…"
          · fallback (postMessage blocked) → "Copied · paste with ⌘/Ctrl+V" ── */}
      {chipToast && (
        <div
          data-testid="corp-dash-mira-chip-toast"
          role="status"
          aria-live="polite"
          style={{
            position: "fixed", bottom: 28, left: "50%",
            transform: "translateX(-50%)",
            background: T.ink, color: T.bg,
            borderRadius: 999, padding: "13px 22px",
            fontSize: 13, letterSpacing: "0.02em",
            boxShadow: "0 12px 32px rgba(33,28,21,0.28)",
            display: "flex", alignItems: "center", gap: 12,
            zIndex: 60, maxWidth: "92vw",
            fontFamily: "inherit",
          }}
        >
          <span style={{ color: T.brassSoft, fontSize: 11,
                         letterSpacing: "0.14em", textTransform: "uppercase",
                         fontWeight: 700 }}>
            {chipToast.fallback ? "Copied" : "Sent to Mira"}
          </span>
          <span style={{ opacity: 0.9 }}>
            {chipToast.fallback ? (
              <>Paste it into Mira with <kbd style={{
                background: "rgba(255,255,255,0.14)", padding: "1px 6px",
                borderRadius: 4, fontFamily: "ui-monospace, Menlo, monospace",
                fontSize: 11,
              }}>⌘/Ctrl + V</kbd></>
            ) : (
              <>Mira is curating your ask…</>
            )}
          </span>
        </div>
      )}

      {/* ── PILLAR CARDS GRID ── */}
      <section data-testid="corp-dash-pillars"
               style={{ paddingTop: 24, borderTop: `1px solid ${T.line}` }}>
        <p style={{ letterSpacing: "0.16em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 8px",
                    fontWeight: 700 }}>
          By the Concierge®
        </p>
        <h2 style={{ fontFamily: "'Fraunces', Georgia, serif",
                     fontSize: "clamp(24px, 3.6vw, 34px)", fontWeight: 500,
                     lineHeight: 1.1, color: T.ink, margin: "0 0 28px" }}>
          The Concierge® is on duty,{" "}
          <em style={{ color: T.brass }}>wherever you are.</em>
        </h2>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 18,
        }}>
          {visible.map((p) => (
            <button
              key={p.key}
              data-testid={`corp-dash-pillar-${p.key}`}
              type="button"
              onClick={() => setDrawerPillar(p)}
              style={{
                display: "flex", flexDirection: "column", textAlign: "left",
                background: T.card, border: `1px solid ${T.line}`,
                borderRadius: 16, padding: "22px 22px 20px",
                color: T.ink, cursor: "pointer", fontFamily: "inherit",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 28px rgba(33,28,21,0.10)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ letterSpacing: "0.14em", fontSize: 10.5,
                            color: T.brass, textTransform: "uppercase",
                            marginBottom: 10, fontWeight: 700 }}>
                /{p.key}
              </div>
              <h3 style={{ fontFamily: "'Fraunces', Georgia, serif",
                           fontSize: 20, fontWeight: 500,
                           lineHeight: 1.2, margin: "0 0 10px" }}>
                {p.title}
                {" "}
                <em style={{ color: T.brass }}>{p.accent}</em>
              </h3>
              <p style={{ color: T.ink2, fontSize: 13, lineHeight: 1.5,
                          margin: "0 0 18px", flex: 1 }}>
                {p.body}
              </p>
              <span style={{
                display: "inline-block", background: T.brass, color: T.bg,
                padding: "9px 16px", borderRadius: 999,
                fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em",
                textTransform: "uppercase", alignSelf: "flex-start",
              }}>
                {p.cta} →
              </span>
            </button>
          ))}

          {/* Clubs · paid upsell · routes to MindEscapes */}
          <a
            data-testid="corp-dash-pillar-club-upsell"
            href="https://mindescapes.club/mindescapes"
            target="_blank"
            rel="noreferrer noopener"
            style={{
              display: "flex", flexDirection: "column",
              background: `${T.brass}0f`,
              border: `1px dashed ${T.brass}`,
              borderRadius: 16, padding: "22px 22px 20px",
              textDecoration: "none", color: T.ink,
              transition: "transform 0.15s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={{ letterSpacing: "0.14em", fontSize: 10.5,
                          color: T.brass, textTransform: "uppercase",
                          marginBottom: 10, fontWeight: 700 }}>
              /clubs · optional
            </div>
            <h3 style={{ fontFamily: "'Fraunces', Georgia, serif",
                         fontSize: 20, fontWeight: 500,
                         lineHeight: 1.2, margin: "0 0 10px" }}>
              A world of <em style={{ color: T.brass }}>private clubs.</em>
            </h3>
            <p style={{ color: T.ink2, fontSize: 13, lineHeight: 1.5,
                        margin: "0 0 18px", flex: 1 }}>
              Worldwide network of members&apos; clubs &mdash; available as a paid
              add-on via MindEscapes. Apply directly.
            </p>
            <span style={{
              display: "inline-block", background: "transparent",
              color: T.brass, border: `1px solid ${T.brass}`,
              padding: "8px 16px", borderRadius: 999,
              fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase", alignSelf: "flex-start",
            }}>
              Apply via MindEscapes ↗
            </span>
          </a>
        </div>
      </section>

      {/* ── Pillar Drawer · slide-in right-side sheet (mirrors MC agent) ── */}
      {drawerPillar && (
        <>
          {/* Backdrop */}
          <div
            data-testid="corp-dash-pillar-drawer-backdrop"
            onClick={() => setDrawerPillar(null)}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(15,12,8,0.42)",
              zIndex: 90,
              animation: "corpDashFadeIn 220ms ease-out",
            }}
          />
          {/* Sheet */}
          <aside
            data-testid={`corp-dash-pillar-drawer-${drawerPillar.key}`}
            role="dialog"
            aria-modal="true"
            aria-label={`${drawerPillar.title} concierge surface`}
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0,
              width: "min(720px, 96vw)",
              background: T.bg,
              boxShadow: "-32px 0 60px rgba(15,12,8,0.28)",
              zIndex: 100, display: "flex", flexDirection: "column",
              animation: "corpDashSlideIn 260ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {/* Drawer header (ink · matches MC "Concierge on Duty" band) */}
            <header style={{
              background: T.ink, color: T.bg,
              padding: "18px 24px",
              display: "flex", alignItems: "center",
              justifyContent: "space-between", gap: 14,
              borderBottom: `1px solid ${T.brass}22`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span aria-hidden style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: "#20C77B",
                  boxShadow: "0 0 0 3px rgba(32,199,123,0.18)",
                }} />
                <span style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 18, fontWeight: 500,
                }}>
                  Concierge<sup style={{ fontSize: 10, verticalAlign: "top" }}>®</sup> on Duty
                </span>
                <span style={{
                  marginLeft: 8, letterSpacing: "0.14em",
                  fontSize: 10, textTransform: "uppercase",
                  color: T.brassSoft, fontWeight: 700,
                }}>
                  /{drawerPillar.key}
                </span>
              </div>
              <button
                data-testid="corp-dash-pillar-drawer-close"
                type="button"
                onClick={() => setDrawerPillar(null)}
                aria-label="Close drawer"
                style={{
                  background: "transparent", color: T.bg,
                  border: 0, cursor: "pointer",
                  fontSize: 22, lineHeight: 1, padding: 4,
                  opacity: 0.85,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              >
                ✕
              </button>
            </header>

            {/* Live pillar surface — embedded from the LC site itself so
                every feature (search, drawer, request form) is available
                inside the corporate portal. Member identity is passed as
                URL params so pillar request forms auto-populate (name /
                email / WhatsApp) and the backend auto-tags every submitted
                brief with `sponsor_company` via the corp session cookie. */}
            <iframe
              data-testid="corp-dash-pillar-drawer-frame"
              title={`${drawerPillar.title} — LC concierge`}
              src={(() => {
                const fullName = [member.first_name, member.last_name]
                                    .filter(Boolean).join(" ") || member.email || "";
                const params = new URLSearchParams({
                  embed:        "drawer",
                  tenant:       `corp:${slug}`,
                  // Unified · fits both `useEmbedMember()` (frontend prefill)
                  // and aurelion_dispatcher (backend identity forwarding).
                  member_name:  fullName,
                  member_first: member.first_name || "",
                  member_last:  member.last_name  || "",
                  member_email: member.email || "",
                  member_phone: member.phone || member.whatsapp || "",
                });
                return `${drawerPillar.href}?${params.toString()}`;
              })()}
              style={{
                flex: 1, width: "100%", border: 0,
                background: T.bg,
              }}
              allow="clipboard-write; geolocation"
            />
          </aside>
          {/* Local keyframes so we don't collide with global animations */}
          <style>{`
            @keyframes corpDashSlideIn {
              from { transform: translateX(24px); opacity: 0; }
              to   { transform: translateX(0);    opacity: 1; }
            }
            @keyframes corpDashFadeIn {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
          `}</style>
        </>
      )}
    </CorporateShell>
  );
}

function greetingFor(_member) {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
